/**
 * Aba Follow-ups: cards da sequência com disparo, mensagem e liga/desliga.
 */
import React from 'react';
import { Button } from '../../../../components/ui/Button';
import Switch from '../../../../components/ui/Switch';
import { PlusIcon } from '../../../../components/icons';
import { AppIcon } from '../../../../components/icons/AppIcon';
import ActionMenu, { createEditAction, createDeleteAction, createDuplicateAction } from '../../../../components/shared/ActionMenu';
import { cn } from '@/lib/utils';
import { formatDelayLabel } from '../followUp.constants';
import { parseMediaUrls } from '../followUpMedia';
import type { FollowUpDelayUnit } from '../types';
import type { FollowUpTemplateDraft } from '../hooks/useFollowUpTemplates';
import FollowUpTriggerFields from './FollowUpTriggerFields';

interface FollowUpListTabProps {
    templates: FollowUpTemplateDraft[];
    loading: boolean;
    saving: boolean;
    onAdd: () => void;
    onEdit: (template: FollowUpTemplateDraft) => void;
    onRemove: (id: string) => void;
    onDuplicate: (template: FollowUpTemplateDraft) => void;
    onToggleActive: (id: string, isActive: boolean) => void;
    onUpdateDelay: (id: string, delayValue: number, delayUnit: FollowUpDelayUnit) => void;
    expandedId: string | null;
    onToggleExpand: (id: string) => void;
    inlineMessage: string;
    onInlineMessageChange: (text: string) => void;
    inlineMediaUrl: string | null;
    onInlineMediaChange: (url: string | null) => void;
    destinationRows: Array<{ id: string; funnelName: string; stageName: string; color?: string | null }>;
}

export default function FollowUpListTab({
    templates, loading, saving, onAdd, onEdit, onRemove, onDuplicate, onToggleActive, onUpdateDelay,
    expandedId, onToggleExpand, inlineMessage, onInlineMessageChange, inlineMediaUrl, onInlineMediaChange,
    destinationRows,
}: FollowUpListTabProps) {
    const destinationBadges = destinationRows.length === 0 ? (
        <span className="badge badge-sm badge-neutral max-w-full border border-border">
            <AppIcon name="kanban" className="size-3.5 shrink-0 text-muted" aria-hidden />
            Todas as etapas
        </span>
    ) : destinationRows.map((row) => {
        const funnelLabel = /funil/i.test(row.funnelName) ? row.funnelName : `Funil ${row.funnelName}`;
        return (
            <span key={row.id} className="badge badge-sm badge-neutral max-w-full min-w-0 border border-border" aria-label={`${funnelLabel}, etapa ${row.stageName}`}>
                <AppIcon name="kanban" className="size-3.5 shrink-0 text-muted" aria-hidden />
                <span className="min-w-0 truncate">{funnelLabel}</span>
                <AppIcon name="arrow-right" className="size-3 shrink-0 text-muted" aria-hidden />
                <span className="max-w-36 truncate sm:max-w-48">{row.stageName}</span>
            </span>
        );
    });

    if (loading) {
        return (
            <section className="flex flex-col gap-s4" aria-label="Lista de follow-ups" aria-busy="true">
                <p className="text-sm text-secondary" role="status">Carregando follow-ups...</p>
            </section>
        );
    }

    if (templates.length === 0) {
        return (
            <section className="flex flex-col gap-s4" aria-label="Lista de follow-ups">
                <div className="flex flex-col items-center gap-s3 rounded-xl border border-dashed border-border bg-surface px-s6 py-s10 text-center">
                    <div className="flex size-11 items-center justify-center rounded-xl bg-primary-subtle text-primary">
                        <PlusIcon className="size-5" aria-hidden />
                    </div>
                    <h3 className="text-sm font-medium text-default">Nenhum follow-up configurado</h3>
                    <p className="text-sm text-muted">Crie o primeiro follow-up para reativar leads automaticamente.</p>
                    <Button type="button" onClick={onAdd} disabled={saving} className="min-h-11 touch-manipulation md:min-h-9">
                        <PlusIcon className="size-4" aria-hidden />
                        Novo follow-up
                    </Button>
                </div>
            </section>
        );
    }

    return (
        <section className="flex flex-col gap-s2" aria-label="Lista de follow-ups">
            <p className="self-end px-s4 font-mono text-sm tabular-nums text-muted">
                {templates.length} {templates.length === 1 ? 'follow-up' : 'follow-ups'}
            </p>
            <ol className="flex flex-col gap-s3">
                {templates.map((template, index) => {
                    const delayLabel = formatDelayLabel(template.delayValue, template.delayUnit);
                    const expanded = expandedId === template.id;
                    const active = template.isActive !== false;
                    const count = (template.message.trim() ? 1 : 0) + parseMediaUrls(template.mediaUrl).length;

                    return (
                        <li key={template.id} value={index + 1}>
                            <article
                                className="rounded-xl border border-border border-t-2 border-t-primary bg-surface transition-shadow hover:border-primary/30 hover:border-t-primary hover:shadow-sm"
                            >
                                <div className="flex flex-col gap-s3 px-s4 py-s3">
                                    <div className="flex flex-col gap-s3 sm:flex-row sm:items-center">
                                        <header className="min-w-0 flex-1">
                                            <div className="flex max-w-full items-center gap-s2">
                                                <button
                                                    type="button"
                                                    className={cn(
                                                        'inline-flex min-h-11 min-w-0 touch-manipulation items-center gap-s2 text-left md:min-h-0',
                                                        !active && 'opacity-60',
                                                    )}
                                                    onClick={() => onToggleExpand(template.id)}
                                                    aria-expanded={expanded}
                                                    aria-label={expanded ? 'Fechar disparo' : 'Abrir disparo'}
                                                >
                                                    <AppIcon name={expanded ? 'arrow-down' : 'arrow-right'} className="size-4 shrink-0 text-primary" aria-hidden />
                                                    <h3 className="m-0 truncate text-sm font-medium text-default md:text-md">Follow-up {index + 1}</h3>
                                                </button>
                                                <span className={cn('badge badge-sm shrink-0', active ? 'badge-success' : 'badge-danger')}>{active ? 'Ativo' : 'Inativo'}</span>
                                            </div>
                                            <p className={cn('mt-s1 text-sm text-muted', !active && 'opacity-60')}>
                                                <button type="button" className="min-h-11 text-left touch-manipulation hover:text-default md:min-h-0" onClick={() => onEdit(template)}>
                                                    Enviado {delayLabel} após a última mensagem do lead
                                                </button>
                                            </p>
                                        </header>

                                        <aside className="flex max-w-full shrink-0 flex-wrap items-center justify-end gap-s2 self-end sm:self-center" aria-label="Ações do follow-up">
                                            <button
                                                type="button"
                                                className="badge badge-sm badge-brand min-h-11 touch-manipulation md:min-h-0"
                                                onClick={() => { if (!expanded) onToggleExpand(template.id); }}
                                                aria-label="Editar mensagem de follow-up"
                                            >
                                                <AppIcon name="message" className="size-3.5" aria-hidden />
                                                {count}
                                            </button>
                                            <span className="badge badge-sm badge-neutral" aria-label={`Disparo em ${delayLabel}`}>
                                                <AppIcon name="clock" className="size-3.5" aria-hidden />
                                                {delayLabel}
                                            </span>
                                            {destinationBadges}
                                            <Switch checked={active} onChange={(checked) => onToggleActive(template.id, checked)} disabled={saving} aria-label={active ? 'Desativar follow-up' : 'Ativar follow-up'} />
                                            <ActionMenu actions={[createEditAction(() => onEdit(template)), createDuplicateAction(() => onDuplicate(template)), createDeleteAction(() => onRemove(template.id))]} />
                                        </aside>
                                    </div>

                                    {expanded && (
                                        <div className={cn(!active && 'opacity-60')}>
                                            <FollowUpTriggerFields
                                                fieldId={template.id}
                                                delayValue={template.delayValue}
                                                delayUnit={template.delayUnit}
                                                onDelayValueChange={(value) => onUpdateDelay(template.id, value, template.delayUnit)}
                                                onDelayUnitChange={(unit) => onUpdateDelay(template.id, template.delayValue, unit)}
                                                message={inlineMessage}
                                                onMessageChange={onInlineMessageChange}
                                                mediaUrl={inlineMediaUrl}
                                                onMediaChange={onInlineMediaChange}
                                            />
                                        </div>
                                    )}
                                </div>
                            </article>
                        </li>
                    );
                })}
            </ol>
        </section>
    );
}
